"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { stegaClean } from "@sanity/client/stega";

const SCROLL_MILESTONES = [25, 50, 75, 100];
const VIDEO_MILESTONES = [25, 50, 75, 100];

type SectionInfo = { section: string; section_index: number };

function cleanText(text: string | null | undefined, max = 80) {
  const clean = (stegaClean(text ?? "") ?? "").replace(/\s+/g, " ").trim();
  const half = clean.slice(0, (clean.length - 1) / 2);
  const label = clean.length % 2 === 1 && clean === `${half} ${half}` ? half : clean;
  return label.slice(0, max);
}

function isSiteChrome(element: Element, tag: "header" | "footer") {
  const chrome = element.closest(tag);
  return (chrome !== null && chrome.closest("main") === null) || (tag === "header" && element.closest("#mobile-menu") !== null);
}

function pageHeadings() {
  return Array.from(document.querySelectorAll<HTMLElement>("h1, h2")).filter(
    (heading) =>
      heading.getClientRects().length > 0 &&
      !isSiteChrome(heading, "header") &&
      !isSiteChrome(heading, "footer") &&
      !heading.closest("[role='dialog'], [data-ph-section]") &&
      cleanText(heading.innerText) !== "",
  );
}

function sectionOf(element: Element): SectionInfo {
  const tagged = element.closest<HTMLElement>("[data-ph-section]")?.dataset.phSection;
  if (tagged) return { section: tagged, section_index: tagged === "footer" ? -1 : 0 };
  if (isSiteChrome(element, "header")) return { section: "header", section_index: 0 };
  if (isSiteChrome(element, "footer")) return { section: "footer", section_index: -1 };

  const dialog = element.closest("[role='dialog']");
  if (dialog) {
    const title = Array.from(dialog.querySelectorAll<HTMLElement>("h1, h2, h3"))
      .map((heading) => cleanText(heading.innerText))
      .find(Boolean);
    return { section: title ? `popup: ${title}` : "popup", section_index: -1 };
  }

  const headings = pageHeadings();
  let index = -1;
  headings.forEach((heading, i) => {
    if (
      heading.contains(element) ||
      heading.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      index = i;
    }
  });

  return index < 0
    ? { section: "top", section_index: 0 }
    : { section: cleanText(headings[index].innerText), section_index: index + 1 };
}

function destinationOf(href: string) {
  if (href.startsWith("#")) return "anchor";
  try {
    const url = new URL(href, window.location.href);
    if (url.hostname === "trademania.app" || url.hostname.endsWith(".trademania.app")) return "app";
    if (url.origin === window.location.origin) {
      return url.pathname === window.location.pathname && url.hash ? "anchor" : "internal";
    }
    return "external";
  } catch {
    return "external";
  }
}

function urlWithoutQuery(href: string) {
  try {
    const url = new URL(href, window.location.href);
    if (!url.protocol.startsWith("http")) return href.split("?")[0];
    return url.origin + url.pathname + url.hash;
  } catch {
    return href.split("?")[0];
  }
}

function videoInfo(video: HTMLVideoElement) {
  const embed = video.closest("[id^='vidalytics_embed_']");
  const taggedId = video.closest<HTMLElement>("[data-ph-video-id]")?.dataset.phVideoId;
  const source =
    taggedId ||
    (video.currentSrc && !video.currentSrc.startsWith("blob:") ? urlWithoutQuery(video.currentSrc) : undefined);

  return {
    video_provider: embed ? "vidalytics" : "native",
    video_id: embed ? embed.id.replace("vidalytics_embed_", "") : source,
    video_duration: Number.isFinite(video.duration) ? Math.round(video.duration) : undefined,
    ...sectionOf(video),
  };
}

export default function PostHogEngagementTracker(): null {
  const pathname = usePathname();
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog) return;

    const scrollReached = new Set<number>();
    const sectionsViewed = new WeakSet<Element>();
    const videosStarted = new WeakSet<HTMLVideoElement>();
    const videoProgress = new WeakMap<HTMLVideoElement, Set<number>>();

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const cta = target?.closest<HTMLElement>("[data-ph-cta], a[href]");
      if (!cta || cta.closest("[id^='vidalytics_embed_']")) return;

      const link = cta.matches("a[href]") ? cta : cta.querySelector<HTMLAnchorElement>("a[href]");
      const href = link?.getAttribute("href") ?? cta.dataset.phTarget ?? undefined;
      const action = cta.dataset.phCta ?? "link";

      posthog.capture("cta_clicked", {
        cta_text:
          cleanText(cta.innerText) ||
          cleanText(cta.getAttribute("aria-label")) ||
          cleanText(cta.querySelector("img")?.alt) ||
          undefined,
        cta_url: href ? urlWithoutQuery(href) : undefined,
        cta_action: action,
        cta_destination: action === "anchor" ? "anchor" : href ? destinationOf(href) : undefined,
        ...sectionOf(cta),
      });
    };

    const onScroll = () => {
      if (scrollReached.size === SCROLL_MILESTONES.length) return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = (window.scrollY / scrollable) * 100;
      SCROLL_MILESTONES.forEach((milestone) => {
        if (percent >= (milestone === 100 ? 98 : milestone) && !scrollReached.has(milestone)) {
          scrollReached.add(milestone);
          posthog.capture("scroll_depth_reached", { percent: milestone });
        }
      });
    };

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || sectionsViewed.has(entry.target)) return;
          sectionsViewed.add(entry.target);
          sectionObserver.unobserve(entry.target);
          posthog.capture("section_viewed", sectionOf(entry.target));
        });
      },
      { rootMargin: "0px 0px -35% 0px" },
    );

    const observeHeadings = () => {
      pageHeadings().forEach((heading) => {
        if (!sectionsViewed.has(heading)) sectionObserver.observe(heading);
      });
    };

    let rescanTimer: ReturnType<typeof setTimeout> | undefined;
    const headingWatcher = new MutationObserver(() => {
      clearTimeout(rescanTimer);
      rescanTimer = setTimeout(observeHeadings, 500);
    });

    const onVideoEvent = (event: Event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || video.paused) return;

      if (!videosStarted.has(video)) {
        if (video.muted || video.volume === 0) return;
        videosStarted.add(video);
        videoProgress.set(video, new Set());
        posthog.capture("video_started", videoInfo(video));
        return;
      }

      if (event.type !== "timeupdate" || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const reached = videoProgress.get(video)!;
      const percent = (video.currentTime / video.duration) * 100;
      VIDEO_MILESTONES.forEach((milestone) => {
        if (percent >= (milestone === 100 ? 98 : milestone) && !reached.has(milestone)) {
          reached.add(milestone);
          posthog.capture("video_progress", { percent: milestone, ...videoInfo(video) });
        }
      });
    };

    const videoEvents = ["play", "timeupdate", "volumechange"];

    document.addEventListener("click", onClick, true);
    window.addEventListener("scroll", onScroll, { passive: true });
    videoEvents.forEach((type) => document.addEventListener(type, onVideoEvent, true));
    observeHeadings();
    headingWatcher.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("scroll", onScroll);
      videoEvents.forEach((type) => document.removeEventListener(type, onVideoEvent, true));
      clearTimeout(rescanTimer);
      headingWatcher.disconnect();
      sectionObserver.disconnect();
    };
  }, [pathname, posthog]);

  return null;
}
